import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useConfirmation } from '../../../hooks/use-confirmation';
import { confirmEmailOtp, disableEmailOtp, startEmailOtp } from '../security.api';

/** `displayedCode` is set only on a test deployment that shows codes instead of emailing them. */
export type EmailOtpSetupState = { phase: 'idle' } | { phase: 'code'; displayedCode: string | null } | { phase: 'recovery-codes'; codes: string[] };

/**
 * Turning email codes on: idle → a code is emailed → it is typed back → recovery codes shown once (only when
 * this is the account's first second factor) → back to idle, on. Turning off goes through "Confirm it's you".
 */
export function useEmailOtp() {
  const { state, setUser } = useCurrentUser();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [setup, setSetup] = useState<EmailOtpSetupState>({ phase: 'idle' });
  const { error, submitting, submit, clearError } = useSubmission();

  const user = state.status === 'authenticated' ? state.user : undefined;

  const sendCode = () =>
    submit(
      () => startEmailOtp(),
      (err) => apiErrorMessage(err) ?? "Couldn't send the code. Try again.",
    );

  function finish() {
    if (user) setUser({ ...user, emailOtpEnabled: true });
    setSetup({ phase: 'idle' });
    toast.success('Email codes turned on');
  }

  async function verify(code: string) {
    const result = await submit(
      () => confirmEmailOtp(code),
      () => 'Incorrect code. Check the newest email and try again.',
    );
    if (!result) return;
    if (result.recoveryCodes.length === 0) finish();
    else setSetup({ phase: 'recovery-codes', codes: result.recoveryCodes });
  }

  async function disable() {
    const isDisabled = await submit(
      () => confirmation.confirmed('turn off email codes', () => disableEmailOtp().then(() => true)),
      (err) => apiErrorMessage(err) ?? "Couldn't turn off email codes. Try again.",
    );
    if (!isDisabled) return;
    if (user) setUser({ ...user, emailOtpEnabled: false });
    toast.success('Email codes turned off');
  }

  return {
    setup,
    error,
    submitting,
    confirmation,
    start: async () => {
      const sent = await sendCode();
      if (sent) setSetup({ phase: 'code', displayedCode: sent.displayedCode });
    },
    resend: async () => {
      const sent = await sendCode();
      if (!sent) return;
      setSetup({ phase: 'code', displayedCode: sent.displayedCode });
      toast.success(sent.displayedCode ? 'New code shown' : `New code sent to ${user?.email ?? 'your email'}`);
    },
    verify,
    finish,
    disable,
    cancel: () => {
      setSetup({ phase: 'idle' });
      clearError();
    },
  };
}
